import {css, useTheme} from '@mui/material';
import {
  CHANGED_BY_OTHER_USER_STATUS,
  useChangedByOtherDialog,
  TranslateUtils,
  ConfigObjectType,
  DLMuiTextButton,
  DlMuiChipButton,
  DlMuiDynamicIcon,
  DlMuiTypography,
  GetStylesFunction,
  useApiMutation,
  utils,
  useLocalStorage,
} from '@wallaby-ui/core';
import * as React from 'react';
import {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';

import {LightRichTextAreaFilter} from '../../../common';
import {usePermissions} from '../../permissions/usePermissions';

export interface PatientCommentsEditOwnProps {
  storageIdPrefix: string;
  patientCardEtag: string | undefined;
  comment: string | undefined;
  commentFormatted: string | undefined;
  setComment: (comment: string | undefined) => void;
  setCommentFormatted: (commentFormatted: string | undefined) => void;
  reloadPatientCard: () => void;
  config: ConfigObjectType;
  isCommentsMaximized: boolean;
  setIsCommentsMaximized: (newValue: boolean) => void;
}

type Props = PatientCommentsEditOwnProps;

export const PatientCommentsEdit: React.FC<Props> = (props) => {
  const {
    storageIdPrefix,
    patientCardEtag,
    comment,
    commentFormatted,
    setComment,
    setCommentFormatted,
    reloadPatientCard,
    config,
    isCommentsMaximized,
    setIsCommentsMaximized,
    ...restProps
  } = props;

  const intl = useIntl();
  const [isEditing, , setIsEditing] = useLocalStorage(`${storageIdPrefix}.isEditingComment`, false);
  const [maximizedBackup, setMaximizedBackup] = useState<{comment?: string; commentFormatted?: string} | undefined>();
  const getCurrentEditorValueRef = useRef<(() => {json: any; html: string; text: string}) | null>(null);

  // Persist the pre-edit snapshot so Discard reverts to the right value after navigating away and back (PDM-2050).
  const [originalComment, , setOriginalComment] = useLocalStorage<string | undefined>(
    `${storageIdPrefix}.originalComment`,
    comment
  );
  const [originalCommentFormatted, , setOriginalCommentFormatted] = useLocalStorage<string | undefined>(
    `${storageIdPrefix}.originalCommentFormatted`,
    commentFormatted
  );

  const theme = useTheme();
  const params = useParams();
  const styles = getStyles({theme, params: {isEditing}});
  const t = (key) => key && TranslateUtils.translate(key, intl);

  const commentApiUrl = `${config.service.url.hospitalizationDetail}/${params.hospitalizationId}/patient-card/comment`;

  const onSuccess = async () => {
    setOriginalComment(comment);
    setOriginalCommentFormatted(commentFormatted);
    reloadPatientCard();
  };

  const {changedByOtherDialog, showChangedByOtherDialog} = useChangedByOtherDialog({
    handleConfirm: () => {
      reloadPatientCard();
    },
  });

  const onError = async (error) => {
    if (error?.response?.status === CHANGED_BY_OTHER_USER_STATUS)
      showChangedByOtherDialog({
        [t('patient.card.comments')]: comment,
      } as Record<string, string>);
    else reloadPatientCard();
  };

  const {mutate} = useApiMutation(
    [commentApiUrl, patientCardEtag],
    {
      comment: comment ?? '',
      commentFormatted: commentFormatted ?? '',
    },
    {
      reactMutationOptions: {
        onSuccess,
        onError,
      },
      axiosConfig: {
        method: 'PATCH',
        url: commentApiUrl,
        headers: {},
      },
    },
    true
  );

  const handleOnClickEdit = () => {
    setOriginalComment(comment);
    setOriginalCommentFormatted(commentFormatted);
    setIsEditing(true);
  };

  const handleDiscardComment = () => {
    setIsEditing(false);
    setIsCommentsMaximized(false);
    setComment(originalComment);
    setCommentFormatted(originalCommentFormatted);
  };

  const handleSaveComment = () => {
    if (getCurrentEditorValueRef.current) {
      const currentValue = getCurrentEditorValueRef.current();
      setComment(currentValue.text ?? undefined);
      setCommentFormatted(currentValue.text ? JSON.stringify(currentValue.json) : undefined);
      setTimeout(() => {
        setIsEditing(false);
        setIsCommentsMaximized(false);
        mutate();
      }, 0);
    } else {
      setIsEditing(false);
      setIsCommentsMaximized(false);
      mutate();
    }
  };

  const handleOnChangeComment = (text: string | undefined, json: string | undefined) => {
    setComment(text);
    setCommentFormatted(json);
  };

  const toggleMaximized = () => {
    if (!isCommentsMaximized) {
      setMaximizedBackup({comment, commentFormatted});
    } else {
      // eslint-disable-next-line no-lonely-if
      if (maximizedBackup) {
        setComment(maximizedBackup.comment);
        setCommentFormatted(maximizedBackup.commentFormatted);
        setMaximizedBackup(undefined);
      }
    }
    setIsCommentsMaximized(!isCommentsMaximized);
  };

  const {userRoles} = usePermissions(config);
  const parseFormattedFieldValue = (json: string | undefined, text: string | undefined) => {
    const jsonStringValue = json || JSON.stringify(text ?? '');
    return JSON.parse(jsonStringValue);
  };

  const maximalizeIconTooltip =
    intl && utils.TranslateUtils.translate(isCommentsMaximized ? 'patient.card.minimalize' : 'patient.card.maximalize', intl);

  return (
    <div css={styles.editableFieldWrapper} {...restProps}>
      <div css={styles.headerWrapper}>
        <DlMuiTypography css={styles.patientComments} variant={'label'} value={'patient.card.comments'} />
        {(isEditing || isCommentsMaximized) && (
          <DlMuiDynamicIcon
            iconName='maximizeIcon'
            style={{
              color: 'var(--dlMultiColorSecondary)',
              transform: isCommentsMaximized ? 'rotate(180deg)' : undefined,
            }}
            iconTitle={maximalizeIconTooltip}
            onClick={toggleMaximized}
            useFixedViewBox
            inputCss={styles.maximizeIcon}
            svgIconInputCss={styles.svgMaximizeIcon}
            noButtonStyles
          />
        )}
      </div>
      {!comment && !isEditing && userRoles.ENCOUNTER_DETAIL_EDIT_ROLE && (
        <DLMuiTextButton title={'patient.card.addComment'} onClick={handleOnClickEdit} />
      )}
      <div css={styles.commentWrapper}>
        <div css={styles.comment} style={isCommentsMaximized ? {height: '100%'} : {}}>
          <LightRichTextAreaFilter
            config={{name: 'comments', ui: {}}}
            optional
            value={parseFormattedFieldValue(commentFormatted, comment)}
            onChange={(json, html, text) => handleOnChangeComment(text ?? undefined, text ? JSON.stringify(json) : undefined)}
            onGetCurrentValue={(getValueFn) => {
              getCurrentEditorValueRef.current = getValueFn;
            }}
            isDefaultValue={commentFormatted === originalCommentFormatted}
            disabled={!isEditing}
            slotProps={{
              Root: {
                style: {
                  padding: '8px 0',
                  maxWidth: '100%',
                  flex: 1,
                  opacity: isEditing ? 1 : 0.65,
                  filter: isEditing ? 'none' : 'brightness(1.15) contrast(0.9)',
                },
              },
            }}
          />
          {isEditing ? (
            <div css={styles.buttonsWrapper}>
              <DlMuiChipButton onClick={handleDiscardComment} variant={'outlined'} title={'patient.card.chipButton.discard'} />
              <DlMuiChipButton
                onClick={handleSaveComment}
                inputCss={styles.filledButton}
                variant={'filled'}
                title={'patient.card.chipButton.save'}
              />
            </div>
          ) : (
            userRoles.ENCOUNTER_DETAIL_EDIT_ROLE && <DLMuiTextButton title={'patient.card.editComment'} onClick={handleOnClickEdit} />
          )}
        </div>
      </div>
      {changedByOtherDialog}
    </div>
  );
};

PatientCommentsEdit.displayName = 'PatientCommentsEdit';

const getStyles: GetStylesFunction = (options) => {
  const isEditing = options?.params?.isEditing;
  return {
    editableFieldWrapper: css({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: `${isEditing ? options?.theme?.spacing(1) : options?.theme?.spacing(2)}`,
      padding: `${options?.theme?.spacing(2, 3)}`,
      width: '100%',
      height: '100%',
      backgroundColor: `${isEditing ? options?.theme?.palette.custom.coolGray['50'] : options?.theme?.palette.custom.white}`,
      borderRadius: `${isEditing ? options?.theme?.spacing(4) : options?.theme?.spacing(0)}`,
    }),
    headerWrapper: css`
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `,
    maximizeIcon: css`
      padding: 0;
      cursor: pointer;
    `,
    svgMaximizeIcon: css`
      cursor: pointer;
    `,
    commentWrapper: css`
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: ${options?.theme?.spacing(2)};
      height: 100%;
      overflow: auto;
    `,
    buttonsWrapper: css`
      display: flex;
      flex-direction: row;
      gap: ${options?.theme?.spacing(3)};
      justify-content: center;
    `,
    comment: css`
      display: flex;
      flex-direction: column;
      gap: ${options?.theme?.spacing(2)};
      width: 100%;
      height: 100%;
      overflow: auto;
    `,
    dlMuiTextField: css`
      width: 100%;
      height: 100%;
      background: none;
    `,
    filledButton: css({
      'backgroundColor': 'var(--dlTheme-500)',
      'color': `${options?.theme?.palette.custom.white}`,

      '&:hover, &:focus': {
        backgroundColor: 'var(--dlTheme-600)',
        color: `${options?.theme?.palette.custom.white}`,
      },

      '&:active': {
        backgroundColor: 'var(--dlTheme-700)',
        color: `${options?.theme?.palette.custom.white}`,
      },
    }),
    commentText: css`
      white-space: pre-wrap;
      word-break: break-word;
      height: 100%;
    `,
  };
};
